import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { useAdmin } from '../context/admin';
import { useAuth } from '../context/auth';
import { useQueryNav } from '../lib/nav';
import { COMPANY_STATUSES, NAME_MAX, companyStatus } from '../lib/access';
import type { AdminUser, AdminWell, Company, CompanyStatus } from '../lib/access';
import {
  ADMIN_SIMULATED,
  deleteCompany,
  loadExample,
  resetAdminStore,
  saveCompany,
  saveUser,
} from '../lib/api/admin';
import type { CompanyInput } from '../lib/api/admin';
import { BRAND_NAME } from '../lib/brand';
import { Dialog, StatusChoice } from './admin/parts';
import { useAdminAction } from './admin/useAdminAction';
import { SkeletonTable } from '../components/ui/Skeleton';

type View = 'list' | 'grid';
type WellsFilter = 'all' | 'with' | 'without';

const PAGE_SIZE = 10;

/** The operator the example data is filed under: this portal's own, when it has one. */
const EXAMPLE_COMPANY = BRAND_NAME === 'WELLXP' ? 'Example Energy' : BRAND_NAME;

/** The other pages of the admin area, as tabs beside this one. */
const TABS = [
  { label: 'Wells', to: '/web/Admin/Wells' },
  { label: 'Roles', to: '/web/Admin/Roles' },
  { label: 'Layouts', to: '/web/Admin/Layouts' },
  { label: 'Users', to: '/web/Admin/Users' },
];

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** One company with everything the page shows about it. */
interface Row {
  company: Company;
  status: CompanyStatus;
  wells: AdminWell[];
  people: AdminUser[];
  admins: AdminUser[];
  /** People each admin looks after, by the admin's id. */
  managed: Map<number, number>;
}

/**
 * Who looks after whom. A person counts for the Company Admin who created
 * them, directly or through the Well Admin in between. Someone no admin
 * created — set up by the superadmin — counts for the company's first admin,
 * so the numbers of a company always add up to its people.
 */
function managedCounts(people: AdminUser[], admins: AdminUser[]): Map<number, number> {
  const byEmail = new Map(people.map((u) => [u.email, u]));
  const adminIds = new Set(admins.map((a) => a.id));
  const counts = new Map(admins.map((a) => [a.id, 0]));

  for (const person of people) {
    if (adminIds.has(person.id)) continue;

    let owner: number | undefined;
    const seen = new Set<number>();
    for (let at: AdminUser | undefined = person; at && !seen.has(at.id); ) {
      seen.add(at.id);
      const by = byEmail.get(at.createdBy);
      if (by && adminIds.has(by.id)) {
        owner = by.id;
        break;
      }
      at = by;
    }

    const key = owner ?? admins[0]?.id;
    if (key != null) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * Where a superadmin starts, and where companies are managed.
 *
 * Everybody else belongs to one company and starts on its wells. A superadmin
 * belongs to none and sees into all of them, so the companies come first:
 * who they are, who administers each, and the way into each one's wells.
 */
export function CompaniesHome() {
  const { access, store, allWells, storeLoaded, wellsLoaded, wellsError, commit, previewAs } = useAdmin();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { params, set } = useQueryNav();
  const { busy, error, notice, run, clear } = useAdminAction();

  // List or grid survives a refresh, so it sits in the URL; the default leaves no trace.
  const view: View = params.get('view') === 'grid' ? 'grid' : 'list';
  const setView = (next: View) => set({ view: next === 'list' ? null : next }, { replace: true });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CompanyStatus | 'all'>('all');
  const [wellsFilter, setWellsFilter] = useState<WellsFilter>('all');
  const [page, setPage] = useState(1);
  /** Companies somebody unfolded or folded by hand; the rest follow the default. */
  const [unfolded, setUnfolded] = useState<Map<number, boolean>>(new Map());
  const [dialog, setDialog] = useState<
    | null
    | { kind: 'company'; company: Company | null }
    | { kind: 'admin'; company: Company; admin: AdminUser | null }
  >(null);
  const [asking, setAsking] = useState(false);

  // The role a company's admins hold: whichever one creates Well Admins.
  const adminRole = store.roles.find((r) => r.permissions.includes('welladmins.manage'));

  const rows = useMemo<Row[]>(
    () =>
      store.companies.map((company) => {
        const people = store.users.filter((u) => u.companyId === company.id);
        const admins = people.filter((u) => u.roleId === adminRole?.id).sort((a, b) => a.id - b.id);
        return {
          company,
          status: companyStatus(company),
          wells: allWells.filter((w) => w.companyId === company.id),
          people,
          admins,
          managed: managedCounts(people, admins),
        };
      }),
    [store.companies, store.users, allWells, adminRole?.id],
  );

  const filtered = rows.filter((row) => {
    const q = search.trim().toLowerCase();
    const { company } = row;
    if (q && !`${company.name} ${company.legalName ?? ''} ${company.code}`.toLowerCase().includes(q)) return false;
    if (statusFilter !== 'all' && row.status !== statusFilter) return false;
    if (wellsFilter === 'with' && row.wells.length === 0) return false;
    if (wellsFilter === 'without' && row.wells.length > 0) return false;
    return true;
  });

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const shown = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  // Only the superadmin sees across companies; anyone else has their wells.
  if (access.kind !== 'superadmin') return <Navigate to="/web/MyWells" replace />;

  const known = new Set(store.companies.map((c) => c.id));
  // Wells nobody has filed under a company yet. They stay reachable from here,
  // or the only way to open one would be to know its id.
  const unfiled = allWells.filter((w) => w.companyId == null || !known.has(w.companyId));

  // Unfolded by default when there is more than one admin to show. A choice
  // made by hand is kept as it is, so adding an admin to a row somebody just
  // unfolded does not fold it again.
  const expanded = (row: Row) => unfolded.get(row.company.id) ?? row.admins.length > 1;
  const toggle = (row: Row) => setUnfolded((have) => new Map(have).set(row.company.id, !expanded(row)));

  const filter = (apply: () => void) => {
    apply();
    setPage(1);
  };

  const openDialog = (next: NonNullable<typeof dialog>) => {
    clear();
    setDialog(next);
  };
  const detail = (company: Company) => navigate(`/web/MyWells?company=${company.id}`);

  async function onSaveCompany(company: Company | null, input: CompanyInput) {
    const name = input.name.trim();
    const ok = await run(
      () => saveCompany(company?.id ?? null, input),
      company ? `${name} saved.` : `${name} created. Next: give it a Company Admin.`,
    );
    if (ok) setDialog(null);
  }

  async function onDeleteCompany(company: Company) {
    const ok = await run(() => deleteCompany(company.id), `${company.name} deleted.`);
    if (ok) setDialog(null);
  }

  async function onSaveAdmin(company: Company, admin: AdminUser | null, input: { name: string; email: string; active: boolean }) {
    if (!adminRole) return;
    const ok = await run(
      () =>
        saveUser(
          admin?.id ?? null,
          { ...input, companyId: company.id, roleId: adminRole.id, wellIds: [] },
          { actor: user?.email ?? 'superadmin', companyWellIds: [] },
        ),
      admin ? `${input.name.trim()} saved.` : `${input.name.trim()} is now a Company Admin of ${company.name}.`,
    );
    if (ok) setDialog(null);
  }

  async function clearAll() {
    previewAs(null);
    await commit(resetAdminStore());
    setAsking(false);
  }

  const adminRows = shown.reduce((n, row) => n + Math.max(1, row.admins.length), 0);

  return (
    <div className="app">
      <Header context="wells" />

      <main className="cmpage">
        <header className="cmhead">
          <h1>Company Management</h1>
          <span className="cmcount">
            {store.companies.length} {plural(store.companies.length, 'Company', 'Companies')}
          </span>
          <span className="cmspacer" />
          <button type="button" className="cmbtn cmbtn--primary" onClick={() => openDialog({ kind: 'company', company: null })}>
            + Add Company
          </button>
        </header>

        <nav className="cmtabs" aria-label="Administration">
          <span className="cmtab is-active" aria-current="page">
            Companies
          </span>
          {TABS.map((tab) => (
            <Link key={tab.to} className="cmtab" to={tab.to}>
              {tab.label}
            </Link>
          ))}
        </nav>

        <div className="cmtools">
          <input
            type="search"
            className="cmsearch"
            placeholder="Search company name or code…"
            aria-label="Search company name or code"
            value={search}
            onChange={(e) => filter(() => setSearch(e.target.value))}
          />
          <select
            className="cmselect"
            aria-label="Status"
            value={statusFilter}
            onChange={(e) => filter(() => setStatusFilter(e.target.value as CompanyStatus | 'all'))}
          >
            <option value="all">Status: All</option>
            {COMPANY_STATUSES.map((s) => (
              <option key={s.key} value={s.key}>
                Status: {s.label}
              </option>
            ))}
          </select>
          <select
            className="cmselect"
            aria-label="Wells"
            value={wellsFilter}
            onChange={(e) => filter(() => setWellsFilter(e.target.value as WellsFilter))}
          >
            <option value="all">Wells: All</option>
            <option value="with">Wells: With wells</option>
            <option value="without">Wells: No wells</option>
          </select>

          <span className="cmspacer" />

          <div className="viewas">
            <span>View As</span>
            <button
              type="button"
              className={view === 'grid' ? 'viewas-btn is-active' : 'viewas-btn'}
              title="Grid View"
              aria-label="Grid View"
              aria-pressed={view === 'grid'}
              onClick={() => setView('grid')}
            >
              <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
                <path
                  d="M2 2h4v4H2zM8 2h4v4H8zM14 2h4v4h-4zM2 8h4v4H2zM8 8h4v4H8zM14 8h4v4h-4zM2 14h4v4H2zM8 14h4v4H8zM14 14h4v4h-4z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              type="button"
              className={view === 'list' ? 'viewas-btn is-active' : 'viewas-btn'}
              title="List View"
              aria-label="List View"
              aria-pressed={view === 'list'}
              onClick={() => setView('list')}
            >
              <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
                <path d="M2 4h16M2 8h16M2 12h16M2 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {wellsError && (
          <div className="alert" role="alert">
            {wellsError}
          </div>
        )}
        {error && !dialog && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {notice && !error && (
          <p className="kbnotice" role="status">
            {notice}
          </p>
        )}

        {!storeLoaded && <SkeletonTable rows={5} cols={4} label="Loading companies" />}

        {storeLoaded && store.companies.length === 0 && (
          <div className="cmempty">
            <p>No companies yet. Start with the operator the wells belong to, then give it a Company Admin.</p>
            {/* Example people are for trying the pages out, not for a server. */}
            {ADMIN_SIMULATED && (
              <button
                type="button"
                className="cmbtn"
                disabled={busy || !wellsLoaded}
                onClick={() =>
                  void run(
                    () => loadExample(EXAMPLE_COMPANY, allWells.filter((w) => !w.local).map((w) => w.id)),
                    `Example data added: ${EXAMPLE_COMPANY}, its wells and five people.`,
                  )
                }
              >
                Fill with example data
              </button>
            )}
          </div>
        )}

        {store.companies.length > 0 && filtered.length === 0 && (
          <p className="cmempty">No company matches that search.</p>
        )}

        {shown.length > 0 && view === 'list' && (
          <div className="cmtable-wrap">
            <table className="cmtable">
              <thead>
                <tr>
                  <th>Company name</th>
                  <th>Code</th>
                  <th>Wells</th>
                  <th>Company admins</th>
                  <th title="People each admin created, directly or through the Well Admins they created">
                    Users / admin
                  </th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => {
                  const { company, admins } = row;
                  const [first, ...others] = admins;
                  const open = expanded(row);
                  const add = (
                    <button type="button" className="cmbtn cmbtn--small cmbtn--add" onClick={() => openDialog({ kind: 'admin', company, admin: null })}>
                      + Add Admin
                    </button>
                  );

                  return [
                    <tr key={company.id} className={row.status === 'active' ? undefined : 'is-off'}>
                      <td>
                        <div className="cmname">
                          <button
                            type="button"
                            className="cmchevron"
                            aria-expanded={open}
                            aria-label={open ? `Hide the other admins of ${company.name}` : `Show the other admins of ${company.name}`}
                            onClick={() => toggle(row)}
                          >
                            {open ? '⌄' : '›'}
                          </button>
                          <span>
                            <button type="button" className="cmlink" onClick={() => detail(company)}>
                              {company.name}
                            </button>
                            {company.legalName && <small>{company.legalName}</small>}
                          </span>
                        </div>
                      </td>
                      <td>{company.code}</td>
                      <td>
                        <strong>{wellsLoaded ? row.wells.length : '…'}</strong>{' '}
                        <small>{plural(row.wells.length, 'well')}</small>
                      </td>
                      <td>
                        {first ? (
                          <AdminCell admin={first} onEdit={() => openDialog({ kind: 'admin', company, admin: first })} />
                        ) : (
                          <span className="cmnone">No admin yet {add}</span>
                        )}
                      </td>
                      <td>{first ? <strong>{row.managed.get(first.id) ?? 0}</strong> : '—'}</td>
                      <td>
                        <StatusPill status={row.status} />
                      </td>
                      <td>
                        <span className="cmactions">
                          <button type="button" className="cmbtn cmbtn--small" onClick={() => openDialog({ kind: 'company', company })}>
                            Edit
                          </button>
                          <button type="button" className="cmbtn cmbtn--small" onClick={() => detail(company)}>
                            Detail
                          </button>
                        </span>
                      </td>
                    </tr>,

                    ...(open
                      ? others.map((admin, index) => (
                          <tr key={`${company.id}-${admin.id}`} className="cmsub">
                            <td>
                              <span className="cmsub-label">↳ additional admin</span>
                            </td>
                            <td>—</td>
                            <td>—</td>
                            <td>
                              <AdminCell admin={admin} onEdit={() => openDialog({ kind: 'admin', company, admin })} />
                            </td>
                            <td>
                              <strong>{row.managed.get(admin.id) ?? 0}</strong>
                            </td>
                            <td>
                              <span className={admin.active ? 'cmpill cmpill--active' : 'cmpill cmpill--inactive'}>
                                {admin.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>{index === others.length - 1 && add}</td>
                          </tr>
                        ))
                      : []),

                    // Nothing to unfold yet: the row is where the next admin is added.
                    open && first && others.length === 0 && (
                      <tr key={`${company.id}-add`} className="cmsub">
                        <td>
                          <span className="cmsub-label">↳ additional admin</span>
                        </td>
                        <td>—</td>
                        <td>—</td>
                        <td>
                          <span className="cmnone">None</span>
                        </td>
                        <td>—</td>
                        <td />
                        <td>{add}</td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}

        {shown.length > 0 && view === 'grid' && (
          <div className="cmgrid">
            {shown.map((row) => {
              const { company, admins } = row;
              return (
                <article key={company.id} className={row.status === 'active' ? 'cmcard' : 'cmcard is-off'}>
                  <header>
                    <span>
                      <button type="button" className="cmlink" onClick={() => detail(company)}>
                        {company.name}
                      </button>
                      <small>
                        {company.code}
                        {company.legalName && ` · ${company.legalName}`}
                      </small>
                    </span>
                    <StatusPill status={row.status} />
                  </header>
                  <dl>
                    <div>
                      <dt>Wells</dt>
                      <dd>{wellsLoaded ? `${row.wells.length} ${plural(row.wells.length, 'well')}` : '…'}</dd>
                    </div>
                    <div>
                      <dt>People</dt>
                      <dd>
                        {row.people.length} {plural(row.people.length, 'person', 'people')}
                      </dd>
                    </div>
                    <div>
                      <dt>Admins</dt>
                      <dd>
                        {admins.length === 0 && <span className="cmnone">No admin yet</span>}
                        {admins.map((admin) => (
                          <AdminCell
                            key={admin.id}
                            admin={admin}
                            count={row.managed.get(admin.id) ?? 0}
                            onEdit={() => openDialog({ kind: 'admin', company, admin })}
                          />
                        ))}
                      </dd>
                    </div>
                  </dl>
                  <footer className="cmactions">
                    <button type="button" className="cmbtn cmbtn--small" onClick={() => openDialog({ kind: 'company', company })}>
                      Edit
                    </button>
                    <button type="button" className="cmbtn cmbtn--small" onClick={() => detail(company)}>
                      Detail
                    </button>
                    <button type="button" className="cmbtn cmbtn--small cmbtn--add" onClick={() => openDialog({ kind: 'admin', company, admin: null })}>
                      + Add Admin
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        )}

        {filtered.length > 0 && (
          <footer className="cmfoot">
            <span>
              Showing {shown.length} of {filtered.length} {plural(filtered.length, 'company', 'companies')} · {adminRows}{' '}
              admin {plural(adminRows, 'row')}
            </span>
            <span className="cmspacer" />
            {pages > 1 && (
              <span className="cmpager">
                <button type="button" className="cmbtn cmbtn--small" disabled={current === 1} onClick={() => setPage(current - 1)}>
                  Previous
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={n === current ? 'cmbtn cmbtn--small cmbtn--primary' : 'cmbtn cmbtn--small'}
                    aria-current={n === current ? 'page' : undefined}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button type="button" className="cmbtn cmbtn--small" disabled={current === pages} onClick={() => setPage(current + 1)}>
                  Next
                </button>
              </span>
            )}
          </footer>
        )}

        {wellsLoaded && unfiled.length > 0 && (
          <p className="cmunfiled">
            {unfiled.length} {plural(unfiled.length, 'well is', 'wells are')} not filed under a company yet.{' '}
            <Link to="/web/MyWells?company=none">Open {plural(unfiled.length, 'it', 'them')}</Link>
          </p>
        )}

        {ADMIN_SIMULATED && (
          <p className="cmsim">
            Simulated: companies, roles, layouts and users are kept in this browser only, and a user created here
            cannot sign in yet. They move to the server once the API has these endpoints.{' '}
            {asking ? (
              <>
                Throw away everything on these pages?{' '}
                <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={() => void clearAll()}>
                  Clear
                </button>{' '}
                <button type="button" className="cmbtn cmbtn--small" onClick={() => setAsking(false)}>
                  Keep
                </button>
              </>
            ) : (
              <button type="button" className="adminlink" onClick={() => setAsking(true)}>
                Clear the simulated data
              </button>
            )}
          </p>
        )}
      </main>

      {dialog?.kind === 'company' && (
        <CompanyDialog
          key={dialog.company?.id ?? 'new'}
          company={dialog.company}
          wells={allWells}
          known={known}
          busy={busy}
          error={error}
          onClose={() => setDialog(null)}
          onSave={(input) => void onSaveCompany(dialog.company, input)}
          onDelete={dialog.company ? () => void onDeleteCompany(dialog.company as Company) : undefined}
        />
      )}

      {dialog?.kind === 'admin' && (
        <AdminDialog
          key={`${dialog.company.id}-${dialog.admin?.id ?? 'new'}`}
          company={dialog.company}
          admin={dialog.admin}
          busy={busy}
          error={adminRole ? error : 'There is no role that creates Well Admins. Add one on the Roles page first.'}
          onClose={() => setDialog(null)}
          onSave={(input) => void onSaveAdmin(dialog.company, dialog.admin, input)}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ pieces

function StatusPill({ status }: { status: CompanyStatus }) {
  const label = COMPANY_STATUSES.find((s) => s.key === status)?.label ?? status;
  return <span className={`cmpill cmpill--${status}`}>{label}</span>;
}

const AVATAR_COLOURS = ['#1f6fb2', '#2e9e7d', '#7a5bd0', '#d9822b', '#c0463f', '#3d7f8f', '#5d6d78'];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '')).toUpperCase() || '?';
}

function AdminCell({ admin, count, onEdit }: { admin: AdminUser; count?: number; onEdit: () => void }) {
  const colour = AVATAR_COLOURS[admin.id % AVATAR_COLOURS.length];
  return (
    <span className="cmadmin">
      <span className="cmavatar" style={{ background: colour }} aria-hidden="true">
        {initials(admin.name)}
      </span>
      <button type="button" className="cmadmin-name" title={`Edit ${admin.name} (${admin.email})`} onClick={onEdit}>
        {admin.name}
      </button>
      <span className="cmtag">Admin</span>
      {!admin.active && count !== undefined && <span className="cmpill cmpill--inactive">Inactive</span>}
      {count !== undefined && (
        <small>
          {count} {plural(count, 'user')}
        </small>
      )}
    </span>
  );
}

function CompanyDialog({
  company,
  wells,
  known,
  busy,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  company: Company | null;
  wells: AdminWell[];
  known: Set<number>;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (input: CompanyInput) => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(company?.name ?? '');
  const [legalName, setLegalName] = useState(company?.legalName ?? '');
  const [code, setCode] = useState(company?.code ?? '');
  const [status, setStatus] = useState<CompanyStatus>(company ? companyStatus(company) : 'active');
  /** One entry per row; null is a row whose well has not been picked yet. */
  const [picked, setPicked] = useState<(number | null)[]>(() =>
    company ? wells.filter((w) => w.companyId === company.id).map((w) => w.id) : [],
  );
  const [deleting, setDeleting] = useState(false);

  // A well can be taken in only if it belongs to nobody, or already to this company.
  const free = wells.filter((w) => w.companyId == null || !known.has(w.companyId) || w.companyId === company?.id);
  const optionsFor = (row: number) => free.filter((w) => w.id === picked[row] || !picked.includes(w.id));
  const canAdd = free.some((w) => !picked.includes(w.id)) && !picked.includes(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave({
      name,
      legalName,
      code,
      status,
      active: status === 'active',
      wellIds: picked.filter((id): id is number => id != null),
    });
  }

  return (
    <Dialog
      title={company ? `Edit Company — ${company.name}` : 'New Company'}
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="cmbtn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="cmbtn cmbtn--primary" disabled={busy}>
            {busy ? 'Saving…' : company ? 'Save Changes' : 'Create Company'}
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}

      <label className="cmfield">
        <span>Company Name *</span>
        <input
          type="text"
          value={name}
          maxLength={NAME_MAX}
          required
          autoFocus
          placeholder="e.g. Company name"
          onChange={(e) => setName(e.target.value)}
        />
        <small>Company display name</small>
      </label>

      <div className="cmfields">
        <label className="cmfield">
          <span>Legal Company Name</span>
          <input
            type="text"
            value={legalName}
            maxLength={NAME_MAX}
            placeholder="e.g. Legal name"
            onChange={(e) => setLegalName(e.target.value)}
          />
        </label>
        <label className="cmfield">
          <span>Company Code *</span>
          <input
            type="text"
            value={code}
            maxLength={8}
            required
            placeholder="e.g. PET"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </label>
      </div>

      <StatusChoice legend="Status" name="company-status" options={COMPANY_STATUSES} value={status} onChange={setStatus} />

      <div className="cmwells" role="group" aria-label="Wells">
        <div className="cmwells-head">
          <span>Wells</span>
          <button
            type="button"
            className="cmbtn cmbtn--small cmbtn--primary"
            disabled={!canAdd}
            title={canAdd ? undefined : 'Every well already belongs to a company'}
            onClick={() => setPicked([...picked, null])}
          >
            + Add Well
          </button>
        </div>

        {picked.length === 0 && <p className="cmhint">No wells yet.</p>}

        {picked.map((id, row) => (
          <div className="cmwell" key={row}>
            <select
              aria-label={`Well ${row + 1}`}
              value={id ?? ''}
              onChange={(e) => setPicked(picked.map((p, i) => (i === row ? Number(e.target.value) || null : p)))}
            >
              <option value="">Pick a well</option>
              {optionsFor(row).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="cmtrash"
              aria-label={`Remove well ${row + 1}`}
              title="Remove from this company"
              onClick={() => setPicked(picked.filter((_, i) => i !== row))}
            >
              <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                <path
                  d="M7 3h6l1 2h3v2H3V5h3l1-2Zm-2 6h10l-.7 8.2a1 1 0 0 1-1 .8H6.7a1 1 0 0 1-1-.8L5 9Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        ))}

        <p className="cmhint">
          Wells that belong to no company yet can be added here. A new well is created on the{' '}
          <Link to="/web/Admin/Wells">Wells</Link> tab.
        </p>
      </div>

      {onDelete && (
        <p className="cmdelete">
          {deleting ? (
            <>
              Delete {company?.name}?{' '}
              <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={onDelete} disabled={busy}>
                Delete
              </button>{' '}
              <button type="button" className="cmbtn cmbtn--small" onClick={() => setDeleting(false)}>
                Keep
              </button>
            </>
          ) : (
            <button type="button" className="adminlink" onClick={() => setDeleting(true)}>
              Delete this company
            </button>
          )}
        </p>
      )}
    </Dialog>
  );
}

const ADMIN_STATUSES: { key: 'active' | 'inactive'; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

function AdminDialog({
  company,
  admin,
  busy,
  error,
  onClose,
  onSave,
}: {
  company: Company;
  admin: AdminUser | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (input: { name: string; email: string; active: boolean }) => void;
}) {
  const [name, setName] = useState(admin?.name ?? '');
  const [email, setEmail] = useState(admin?.email ?? '');
  const [status, setStatus] = useState<'active' | 'inactive'>(admin && !admin.active ? 'inactive' : 'active');

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave({ name, email, active: status === 'active' });
  }

  return (
    <Dialog
      title={admin ? 'Edit Company Admin' : 'Add Company Admin'}
      subtitle={admin ? `${company.name} · ${admin.name}` : `${company.name} · admins can be added without limit`}
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="cmbtn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="cmbtn cmbtn--primary" disabled={busy}>
            {busy ? 'Saving…' : admin ? 'Save Changes' : 'Add Admin'}
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}

      <label className="cmfield">
        <span>Admin Full Name *</span>
        <input
          type="text"
          value={name}
          maxLength={NAME_MAX}
          required
          autoFocus
          placeholder="e.g. Rina Hartono"
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <label className="cmfield">
        <span>Work Email *</span>
        <input
          type="email"
          value={email}
          required
          readOnly={admin !== null}
          placeholder="admin@company.com"
          onChange={(e) => setEmail(e.target.value)}
        />
        {admin && <small>How they sign in. Cannot be changed.</small>}
      </label>

      <StatusChoice
        legend={admin ? 'Status' : 'Initial Status'}
        name="admin-status"
        options={ADMIN_STATUSES}
        value={status}
        onChange={setStatus}
      />

      <p className="cminfo">
        {admin
          ? 'Edit this admin’s profile and status. The number of users each admin looks after is shown separately in the list.'
          : `This admin will be added to ${company.name}. You can add multiple admins per company — there is no limit. Each admin’s user count is tracked separately.`}
      </p>
    </Dialog>
  );
}
